// src/components/math/MathUtils.jsx

export class MathUtils {
  static mathJaxLoaded = false;
  static mathJaxPromise = null;

  static initializeMathJax() {
    if (this.mathJaxLoaded) return Promise.resolve();
    if (this.mathJaxPromise) return this.mathJaxPromise;

    this.mathJaxPromise = new Promise((resolve, reject) => {
      const finishLoading = () => {
        const startupPromise = window.MathJax?.startup?.promise;
        const ready = startupPromise && typeof startupPromise.then === 'function'
          ? startupPromise
          : Promise.resolve();

        ready.then(() => {
          if (!window.MathJax?.typesetPromise) {
            throw new Error('MathJax loaded without a typesetting API.');
          }
          this.mathJaxLoaded = true;
          resolve();
        }).catch(reject);
      };

      if (window.MathJax?.typesetPromise) {
        this.mathJaxLoaded = true;
        resolve();
        return;
      }

      // index.html starts MathJax asynchronously. Replacing window.MathJax or
      // injecting a second loader while that request is in flight leaves two
      // competing startup instances and can make display delimiters fail even
      // though inline math happens to render.
      const existingScript = document.getElementById('MathJax-script');
      if (existingScript) {
        existingScript.addEventListener('load', finishLoading, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      window.MathJax = {
        tex: {
          inlineMath: [['\\(', '\\)'], ['$', '$']],
          displayMath: [['\\[', '\\]'], ['$$', '$$']],
          processEscapes: true,
          processEnvironments: true,
          macros: {
            R: '\\mathbb{R}',
            N: '\\mathbb{N}',
            Z: '\\mathbb{Z}',
            Q: '\\mathbb{Q}',
            C: '\\mathbb{C}',
            reals: '\\mathbb{R}',
            naturals: '\\mathbb{N}',
            Integers: '\\mathbb{Z}',
            infin: '\\infty',
            infinity: '\\infty',
            d: '\\mathrm{d}',
            e: '\\mathrm{e}',
            i: '\\mathrm{i}',
            implies: '\\Rightarrow',
            iff: '\\Leftrightarrow',
            grad: '\\nabla',
            div: '\\nabla\\cdot',
            curl: '\\nabla\\times',
            emptyset: '\\varnothing',
            forall: '\\forall',
            exists: '\\exists'
          }
        },
        svg: {
          fontCache: 'global'
        },
        options: {
          enableMenu: false,
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
          ignoreHtmlClass: 'tex2jax_ignore',
          processHtmlClass: 'tex2jax_process'
        },
        startup: {
          typeset: false
        }
      };

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js';
      script.async = true;
      script.id = 'MathJax-script';

      script.onload = finishLoading;

      script.onerror = reject;
      document.head.appendChild(script);
    });

    return this.mathJaxPromise;
  }

  static normalizeAliases(text) {
    if (!text) return text;
    return text
      .replace(/\\Reals\b/g, '\\mathbb{R}')
      .replace(/\\Naturals\b/g, '\\mathbb{N}')
      .replace(/\\Integers\b/g, '\\mathbb{Z}')
      .replace(/\\infin\b/g, '\\infty')
      .replace(/\\infinity\b/g, '\\infty')
      // Arrow shorthands
      .replace(/(?<!\\)(->|-->)/g, '\\to ')
      .replace(/(?<!\\)=>+/g, '\\implies ')
      .replace(/(?<!\\)<->/g, '\\leftrightarrow ')
      .replace(/(?<!\\)<=>/g, '\\iff ');
  }

  static async renderMath(element) {
    if (!element) return;

    // Skip if no text content
    const textContent = element.textContent?.trim();
    if (!textContent) return;

    await this.initializeMathJax();

    if (window.MathJax?.typesetPromise) {
      try {
        await window.MathJax.typesetPromise([element]);
      } catch (err) {
        console.warn('MathJax rendering warning:', err.message);
      }
    }
  }

  static processMathExpressions(text) {
    if (!text) return text;

    let result = text;

    // 1. Normalize delimiters to standard $ and $$
    result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => {
      const trimmed = m.trim();
      return trimmed ? `$$${trimmed}$$` : '';
    });

    result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => {
      const trimmed = m.trim();
      return trimmed ? `$${trimmed}$` : '';
    });

    // 2. Normalize aliases (including shorthands like ->)
    result = this.normalizeAliases(result);

    // 3. Temporarily protect existing math blocks to avoid double-wrapping
    const mathBlocks = [];
    result = result.replace(/(\$\$[\s\S]*?\$\$|\$[^$]+\$)/g, (match) => {
      mathBlocks.push(match);
      return `___MATH_BLOCK_${mathBlocks.length - 1}___`;
    });

    
    // 4. Wrap standalone LaTeX arrow commands in math delimiters
    result = result.replace(/\\(to|rightarrow|Rightarrow|leftarrow|Leftarrow|longrightarrow|Longrightarrow|longleftarrow|Longleftarrow|leftrightarrow|Leftrightarrow|longleftrightarrow|mapsto|implies|impliedby|iff)\b/g, '$\\$1$');

    // 5. Restore protected math blocks
    result = result.replace(/___MATH_BLOCK_(\d+)___/g, (_, index) => {
      return mathBlocks[parseInt(index)];
    });

    // Remove empty math blocks
    result = result.replace(/\$\$\s*\$\$/g, '');
    // Do not mistake either pair of delimiters in $$display math$$ for an
    // empty inline expression.
    result = result.replace(/(?<!\$)\$\s+\$(?!\$)/g, '');

    return result;
  }

  static containsMathExpressions(text) {
    if (!text) return false;

    return [
      /\$\$[\s\S]+?\$\$/,
      /\$[^$]+\$/,
      /->|=>|<->|<=>/,
      /\\(to|rightarrow|leftarrow|leftrightarrow|Rightarrow|Leftarrow|Leftrightarrow|mapsto|hookrightarrow|hookleftarrow|uparrow|downarrow|updownarrow|nearrow|searrow|swarrow|nwarrow|Longrightarrow|Longleftarrow|Longleftrightarrow|longrightarrow|longleftarrow|longleftrightarrow|gets|rightarrowtail|leftarrowtail|twoheadrightarrow|twoheadleftarrow|rightsquigarrow|leftrightsquigarrow|dashrightarrow|dashleftarrow|xrightarrow|xleftarrow|xleftrightarrow|xRightarrow|xLeftarrow|xLeftrightarrow|implies|impliedby|iff)\b/,
      /\\mathbb/,
      /\\infty/,
      /\\Reals/,
      /\\Naturals/,
      /\\Integers/,
      /\\frac/,
      /\\sqrt/,
      /\\sum/,
      /\\int/,
      /\\lim/,
      /\\sin|\\cos|\\tan/,
      /\\leq|\\geq|\\neq/,
      /\\in\b|\\notin\b/,
      /\\cup|\\cap/,
      /\\forall|\\exists/
    ].some(rx => rx.test(text));
  }

  static protectMathExpressions(text) {
    let result = text;

    result = result.replace(/\$\$([\s\S]*?)\$\$/g, m =>
      `\uE000D${encodeURIComponent(m)}\uE001`
    );

    result = result.replace(/(?<!\$)\$(?!\$)([^$]+)\$(?!\$)/g, m =>
      `\uE000I${encodeURIComponent(m)}\uE001`
    );

    return result;
  }

  static restoreMathExpressions(text) {
    return text
      .replace(/\uE000[DI](.*?)\uE001/g, (_, m) =>
        decodeURIComponent(m)
      );
  }

  static convertToExplicitMathJaxDelimiters(html) {
    if (!html) return html;

    // MathJax always recognises its explicit TeX delimiters, independent of
    // whether a preloaded configuration enabled dollar delimiters. Markdown
    // has already been parsed at this point, so converting here also prevents
    // either pair in $$...$$ from being consumed as an empty inline formula.
    return String(html)
      .replace(/\$\$([\s\S]+?)\$\$/g, (_, latex) => `\\[${latex.trim()}\\]`)
      .replace(/(?<!\$)\$([^$]+?)\$(?!\$)/g, (_, latex) => `\\(${latex.trim()}\\)`);
  }

  static async loadMarked() {
    if (window.marked) return;

    await new Promise((resolve) => {
      const s = document.createElement('script');
      const timer = window.setTimeout(resolve, 1500);
      s.src = 'https://cdn.jsdelivr.net/npm/marked@9.1.6/marked.min.js';
      s.onload = () => {
        window.clearTimeout(timer);
        resolve();
      };
      s.onerror = () => {
        window.clearTimeout(timer);
        resolve();
      };
      document.head.appendChild(s);
    });
  }

  static escapeHtml(text) {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  static renderInlineMarkdown(text) {
    let out = this.escapeHtml(text);
    out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    return out;
  }

  static isTableSeparatorRow(line) {
    return /^\s*\|?[\s:-]+(\|[\s:-]+)+\|?\s*$/.test(line);
  }

  static splitPipeCells(line) {
    let text = String(line || '').trim();
    if (text.startsWith('|')) text = text.slice(1);
    if (text.endsWith('|')) text = text.slice(0, -1);
    return text.split('|').map((cell) => cell.trim());
  }

  static processBasicMarkdown(text) {
    const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
    const out = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line) {
        i += 1;
        continue;
      }

      if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
        out.push('<hr>');
        i += 1;
        continue;
      }

      if (/^>\s?/.test(line)) {
        const quote = [];
        while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
          quote.push(lines[i].trim().replace(/^>\s?/, ''));
          i += 1;
        }
        out.push(`<blockquote>${quote.map((item) => `<p>${this.renderInlineMarkdown(item)}</p>`).join('')}</blockquote>`);
        continue;
      }

      const heading = line.match(/^(#{1,6})\s+(.+)$/);
      if (heading) {
        const level = heading[1].length;
        out.push(`<h${level}>${this.renderInlineMarkdown(heading[2])}</h${level}>`);
        i += 1;
        continue;
      }

      if (line.startsWith('|') && i + 1 < lines.length && this.isTableSeparatorRow(lines[i + 1])) {
        const head = this.splitPipeCells(lines[i]);
        i += 2;
        const rows = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          rows.push(this.splitPipeCells(lines[i]));
          i += 1;
        }
        out.push(
          `<table><thead><tr>${head.map((cell) => `<th>${this.renderInlineMarkdown(cell)}</th>`).join('')}</tr></thead><tbody>${rows
            .map((row) => `<tr>${row.map((cell) => `<td>${this.renderInlineMarkdown(cell)}</td>`).join('')}</tr>`)
            .join('')}</tbody></table>`
        );
        continue;
      }

      if (/^\d+\.\s+/.test(line)) {
        const items = [];
        while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
          items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
          i += 1;
        }
        out.push(`<ol>${items.map((item) => `<li>${this.renderInlineMarkdown(item)}</li>`).join('')}</ol>`);
        continue;
      }

      if (/^[*-]\s+/.test(line)) {
        const items = [];
        while (i < lines.length && /^[*-]\s+/.test(lines[i].trim())) {
          items.push(lines[i].trim().replace(/^[*-]\s+/, ''));
          i += 1;
        }
        out.push(`<ul>${items.map((item) => `<li>${this.renderInlineMarkdown(item)}</li>`).join('')}</ul>`);
        continue;
      }

      const para = [];
      while (i < lines.length && lines[i].trim()) {
        const candidate = lines[i].trim();
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(candidate)) break;
        if (/^>\s?/.test(candidate)) break;
        if (/^(#{1,6})\s+/.test(candidate)) break;
        if (/^\d+\.\s+/.test(candidate)) break;
        if (/^[*-]\s+/.test(candidate)) break;
        if (candidate.startsWith('|') && i + 1 < lines.length && this.isTableSeparatorRow(lines[i + 1])) break;
        para.push(candidate);
        i += 1;
      }
      out.push(`<p>${this.renderInlineMarkdown(para.join(' '))}</p>`);
    }

    return out.join('') || '<p><br></p>';
  }

  static processMarkdown(text) {
    if (!window.marked) return this.processBasicMarkdown(this.restoreMathExpressions(text));
    try {
      return window.marked.parse(text);
    } catch (_) {
      return this.processBasicMarkdown(this.restoreMathExpressions(text));
    }
  }

  static async processContentWithMarkdown(text) {
    if (!text) return '';

    await this.loadMarked();

    const normalised = this.processMathExpressions(text);
    const protectedText = this.protectMathExpressions(normalised);
    const html = this.processMarkdown(protectedText);

    return this.restoreMathExpressions(html);
  }
}
